import { db, ensureProject, withSyncApplying } from "./db";
import { deleteEmbeddings } from "./db/vec-store";
import { runRelaxedSearch, runRelaxedSearchAsync } from "./search";
import { offloadAllOrTimeout, READ_JOB_TIMED_OUT } from "./read-offload";
import { sanitizeSurrogates } from "./markdown";
import * as log from "./log";
import * as embedding from "./embedding";
import {
  classifyToolError,
  extractFilePath,
  isVerifierCall,
  MAX_ERROR_MESSAGE_LEN,
} from "./tool-trace";
import type { LoreMessage, LorePart, LoreToolState } from "./types";
import { isTextPart, isReasoningPart, isToolPart } from "./types";

// ~3 chars per token — validated as best heuristic against real API data.
function estimate(text: string): number {
  return Math.ceil(text.length / 3);
}

/**
 * Chunk-boundary terminator inserted between chunks by `partsToText`.
 *
 * `\x1f` is ASCII Unit Separator — a non-word control char that:
 *   - cannot legitimately appear in normal chat or tool content (control
 *     chars are vanishingly rare even in binary file dumps),
 *   - is treated as a token separator by FTS5's `unicode61` tokenizer, so
 *     it has zero effect on BM25 indexing or scoring,
 *   - survives `sanitizeSurrogates()` (which only touches lone UTF-16
 *     surrogates, never ASCII control chars).
 *
 * Placed AFTER the existing `\n` so display tools that split on `\n`
 * still render correctly; the structural parser (in `distillation.ts`)
 * splits on `"\n" + CHUNK_TERMINATOR` for unambiguous chunk recovery.
 *
 * Adopted in F3b. Pre-F3b rows are rewritten in-place by a SQL migration
 * (see `db.ts`); after that migration runs, every `temporal_messages.content`
 * value uses this format consistently.
 */
export const CHUNK_TERMINATOR = "\x1f";

/**
 * Serialize a list of message parts into a single content string for the
 * `temporal_messages.content` column. Chunks are separated by
 * `"\n" + CHUNK_TERMINATOR` so the structural parser can recover chunk
 * boundaries unambiguously regardless of payload contents (including
 * payloads that contain literal `[tool:...]` substrings — e.g. when the
 * agent reads a file that documents this very format).
 *
 * Exported so tests can pin producer/consumer round-trip behavior.
 */
export function partsToText(parts: LorePart[]): string {
  const chunks: string[] = [];
  for (const part of parts) {
    if (isTextPart(part)) chunks.push(part.text);
    else if (isReasoningPart(part) && part.text)
      chunks.push(`[reasoning] ${part.text}`);
    else if (isToolPart(part) && part.state.status === "completed")
      chunks.push(`[tool:${part.tool}] ${part.state.output}`);
  }
  // Sanitize unpaired surrogates from tool outputs and other raw text.
  // Without this, surrogates survive into the DB and later break JSON
  // serialization when included in recall tool responses.
  return sanitizeSurrogates(chunks.join(`\n${CHUNK_TERMINATOR}`));
}

function messageMetadata(info: LoreMessage, parts: LorePart[]): string {
  const meta: Record<string, unknown> = {};
  if (info.role === "user") {
    meta.agent = info.agent;
    meta.model = info.model;
  } else {
    meta.modelID = info.modelID;
    meta.providerID = info.providerID;
    meta.mode = info.mode;
  }
  const tools = parts.filter(isToolPart).map((p) => p.tool);
  if (tools.length) meta.tools = tools;
  return JSON.stringify(meta);
}

export function store(input: {
  projectPath: string;
  info: LoreMessage;
  parts: LorePart[];
}) {
  const pid = ensureProject(input.projectPath);
  const content = partsToText(input.parts);
  if (!content.trim()) return;

  const existing = db()
    .query("SELECT id FROM temporal_messages WHERE id = ?")
    .get(input.info.id);
  if (existing) {
    db()
      .query(
        "UPDATE temporal_messages SET content = ?, tokens = ?, metadata = ? WHERE id = ?",
      )
      .run(
        content,
        estimate(content),
        messageMetadata(input.info, input.parts),
        input.info.id,
      );
    // Re-embed on content update (fire-and-forget)
    if (embedding.isAvailable()) {
      embedding.embedTemporalMessage(input.info.id, content);
    }
    return;
  }

  db()
    .query(
      `INSERT INTO temporal_messages (id, project_id, session_id, role, content, tokens, distilled, created_at, metadata)
       VALUES (?, ?, ?, ?, ?, ?, 0, ?, ?)`,
    )
    .run(
      input.info.id,
      pid,
      input.info.sessionID,
      input.info.role,
      content,
      estimate(content),
      input.info.time.created,
      messageMetadata(input.info, input.parts),
    );

  // Embed new message for vector search (fire-and-forget)
  if (embedding.isAvailable()) {
    embedding.embedTemporalMessage(input.info.id, content);
  }
}

/**
 * Record structured tool-call metadata into the `tool_calls` table.
 *
 * A tool call spans two messages in the Anthropic protocol:
 *   - the assistant's `tool_use` part carries the tool NAME (state typically
 *     `pending` at this point — the result hasn't arrived yet);
 *   - the following turn's user message carries a `tool: "result"` part
 *     (state `completed` or `error`, after the gateway adapter resolves it)
 *     with the OUTCOME, keyed by the same `callID`.
 *
 * Both phases UPSERT on the globally-unique `call_id` primary key: the tool_use
 * seeds the name + `pending`; the result updates status/error/duration in place.
 * The result branch deliberately does NOT overwrite the recorded tool name with
 * the synthetic `"result"` (it preserves the seeded name).
 *
 * This is a SEPARATE function from `store()` because `store()` early-returns on
 * empty `partsToText()` content and `partsToText()` drops error-state tools —
 * so failures would otherwise be invisible.
 *
 * Idempotent on re-store (retries / stream re-delivery) via the UPSERT.
 */
export function recordToolCalls(input: {
  projectPath: string;
  info: LoreMessage;
  parts: LorePart[];
}): void {
  const toolParts = input.parts.filter(isToolPart);
  if (!toolParts.length) return;

  const pid = ensureProject(input.projectPath);
  const createdAt = input.info.time.created;

  // Split into the two phases up front so the seed (tool_use) phase can be
  // flattened into ONE multi-row upsert instead of one INSERT per part. The
  // per-part loop was flagged as an N+1 (LOREAI-GATEWAY-3G, #1178): each tool_use
  // part emitted its own db.query.run span. tool_result parts stay per-part
  // UPDATEs — each carries distinct outcome values keyed by its own call_id, so
  // batching them buys little and complicates the INSERT-vs-UPDATE branching.
  //
  // INVARIANT the phase-split relies on: a single `parts` array never contains a
  // tool_result AND the tool_use seed for the SAME call_id. tool_use blocks land
  // only on assistant messages; tool_result blocks only on user messages (see
  // contentBlockToPart), and each caller passes exactly one message's parts. So
  // reordering "all seeds, then all results" can never move a result ahead of
  // its own seed — the outcome is identical to the old in-order loop. If a future
  // caller ever passes a mixed array, restore in-order processing.
  const seeds = toolParts.filter((p) => p.tool !== "result");
  const resultParts = toolParts.filter((p) => p.tool === "result");

  // Phase A: assistant tool_use parts — seed name + status in a single
  // multi-row INSERT (chunked). May already be resolved (e.g. host delivers
  // completed state directly). On conflict, only overwrite outcome fields when
  // the existing row is still `pending` — never revert a resolved row back to
  // pending on a re-seed (retry / re-delivery). SQLite applies the VALUES rows
  // in order and fires ON CONFLICT row-by-row, so a duplicate call_id within one
  // batch resolves identically to the old sequential loop.
  if (seeds.length) {
    const COLS_PER_ROW = 12;
    // Chunk the multi-row INSERT so a pathological batch can never exceed
    // SQLite's bound-variable ceiling. We use the conservative ~900-param budget
    // from the #796 chunking convention in db.ts (portable across SQLite builds,
    // whose historical minimum ceiling is 999) — well under the ≥3.32 default of
    // 32766. 75 rows × 12 cols = 900 params. A real turn has far fewer tool_use
    // parts; this only matters for a pathological batch.
    const CHUNK = Math.floor(900 / COLS_PER_ROW); // 75
    const rowSql = `(${Array.from({ length: COLS_PER_ROW }, () => "?").join(", ")})`;
    for (let i = 0; i < seeds.length; i += CHUNK) {
      const batch = seeds.slice(i, i + CHUNK);
      const seedStmt = db().query(
        `INSERT INTO tool_calls
           (call_id, message_id, project_id, session_id, tool, status, error_type, error_message, duration_ms, created_at, verifier, input_path)
         VALUES ${Array.from({ length: batch.length }, () => rowSql).join(", ")}
         ON CONFLICT(call_id) DO UPDATE SET
           status = CASE WHEN tool_calls.status = 'pending' THEN excluded.status ELSE tool_calls.status END,
           error_type = CASE WHEN tool_calls.status = 'pending' THEN excluded.error_type ELSE tool_calls.error_type END,
           error_message = CASE WHEN tool_calls.status = 'pending' THEN excluded.error_message ELSE tool_calls.error_message END,
           duration_ms = COALESCE(excluded.duration_ms, tool_calls.duration_ms),
           -- verifier is derived from the tool_use input (stable across re-seeds);
           -- keep the first non-null classification.
           verifier = COALESCE(tool_calls.verifier, excluded.verifier),
           -- input_path is likewise derived from the (stable) tool_use input;
           -- keep the first non-null path so a re-seed never clobbers it.
           input_path = COALESCE(tool_calls.input_path, excluded.input_path)`,
      );
      const params: Array<string | number | null> = [];
      for (const p of batch) {
        const outcome = toolOutcome(p.tool, p.state);
        params.push(
          p.callID,
          input.info.id,
          pid,
          input.info.sessionID,
          p.tool,
          outcome.status,
          outcome.errorType,
          outcome.errorMessage,
          outcome.duration,
          createdAt,
          isVerifierCall(p.state.input) ? 1 : 0,
          extractFilePath(p.state.input) ?? null,
        );
      }
      seedStmt.run(...params);
    }
  }

  // Phase B: user tool_result parts — update outcome by call_id, preserving
  // the previously-seeded tool name and message_id.
  if (resultParts.length) {
    const updateStmt = db().query(
      `UPDATE tool_calls
         SET status = ?, error_type = ?, error_message = ?, duration_ms = ?
       WHERE call_id = ?`,
    );
    for (const p of resultParts) {
      const outcome = toolOutcome(p.tool, p.state);
      updateStmt.run(
        outcome.status,
        outcome.errorType,
        outcome.errorMessage,
        outcome.duration,
        p.callID,
      );
    }
  }
}

/** Derive structured outcome fields from a tool part's state. */
function toolOutcome(
  tool: string,
  st: LoreToolState,
): {
  status: string;
  errorType: string | null;
  errorMessage: string | null;
  duration: number | null;
} {
  let errorType: string | null = null;
  let errorMessage: string | null = null;
  let duration: number | null = null;

  if (st.status === "error") {
    const raw = st.error ?? "";
    errorMessage = raw ? raw.slice(0, MAX_ERROR_MESSAGE_LEN) : null;
    errorType = classifyToolError(tool, raw);
  }
  if (
    (st.status === "completed" || st.status === "error") &&
    typeof st.time?.end === "number" &&
    st.time.end > st.time.start
  ) {
    // Only record a real duration. Some hosts (e.g. the gateway adapter) set
    // start === end as a placeholder — store NULL rather than a fake 0 so
    // aggregations aren't polluted with meaningless zeros.
    duration = st.time.end - st.time.start;
  }
  return { status: st.status, errorType, errorMessage, duration };
}

export type TemporalMessage = {
  id: string;
  project_id: string;
  session_id: string;
  role: string;
  content: string;
  tokens: number;
  distilled: number;
  created_at: number;
  metadata: string;
};

export function undistilled(
  projectPath: string,
  sessionID?: string,
): TemporalMessage[] {
  const pid = ensureProject(projectPath);
  const query = sessionID
    ? "SELECT * FROM temporal_messages WHERE project_id = ? AND session_id = ? AND distilled = 0 ORDER BY created_at ASC"
    : "SELECT * FROM temporal_messages WHERE project_id = ? AND distilled = 0 ORDER BY created_at ASC";
  const params = sessionID ? [pid, sessionID] : [pid];
  return db()
    .query(query)
    .all(...params) as TemporalMessage[];
}

export function bySession(
  projectPath: string,
  sessionID: string,
): TemporalMessage[] {
  const pid = ensureProject(projectPath);
  return db()
    .query(
      "SELECT * FROM temporal_messages WHERE project_id = ? AND session_id = ? ORDER BY created_at ASC",
    )
    .all(pid, sessionID) as TemporalMessage[];
}

/** Per-session aggregate used by historical cost estimation. */
export type SessionTokenAggregate = {
  session_id: string;
  /** Sum of `tokens` across all messages in the session. */
  total_tokens: number;
  /** Metadata of the earliest assistant message (for model detection), or null. */
  first_assistant_metadata: string | null;
};

/**
 * Aggregate per-session token sums and first-assistant metadata for a project
 * in two grouped queries (instead of one `SELECT *` per session).
 *
 * Optionally restricts to sessions whose messages were created on/after
 * `sinceMs` (scope-limiting for historical cost scans). Note the token sum is
 * computed over the same window, which matches the historical scan's intent of
 * only attributing recent activity.
 */
export function aggregateTokensBySession(
  projectPath: string,
  opts?: { sinceMs?: number },
): Map<string, SessionTokenAggregate> {
  const pid = ensureProject(projectPath);
  const sinceMs = opts?.sinceMs ?? 0;

  const result = new Map<string, SessionTokenAggregate>();

  // 1. Token sums per session.
  const tokenRows = db()
    .query(
      `SELECT session_id, SUM(tokens) as total_tokens
       FROM temporal_messages
       WHERE project_id = ? AND created_at >= ?
       GROUP BY session_id`,
    )
    .all(pid, sinceMs) as Array<{ session_id: string; total_tokens: number }>;
  for (const row of tokenRows) {
    result.set(row.session_id, {
      session_id: row.session_id,
      total_tokens: row.total_tokens ?? 0,
      first_assistant_metadata: null,
    });
  }

  // 2. Earliest assistant message metadata per session (for model detection).
  //    Uses a JOIN on a pre-grouped MIN subquery instead of a correlated
  //    subquery — the correlated form is O(N²) on large tables (178K+ rows
  //    causes multi-second hangs even with a project_id filter).
  const metaRows = db()
    .query(
      `SELECT t.session_id, t.metadata
       FROM temporal_messages t
       JOIN (
         SELECT session_id, MIN(created_at) AS min_at
         FROM temporal_messages
         WHERE project_id = ? AND role = 'assistant' AND created_at >= ?
         GROUP BY session_id
       ) m ON m.session_id = t.session_id AND t.created_at = m.min_at
       WHERE t.project_id = ? AND t.role = 'assistant'
       GROUP BY t.session_id`,
    )
    .all(pid, sinceMs, pid) as Array<{
    session_id: string;
    metadata: string;
  }>;
  for (const row of metaRows) {
    const existing = result.get(row.session_id);
    if (existing) {
      existing.first_assistant_metadata = row.metadata;
    } else {
      result.set(row.session_id, {
        session_id: row.session_id,
        total_tokens: 0,
        first_assistant_metadata: row.metadata,
      });
    }
  }

  return result;
}

/**
 * Aggregate per-session token sums and first-assistant metadata across ALL
 * projects in two grouped queries (instead of P per-project calls).
 * Used by historical cost estimation to replace the per-project loop.
 *
 * INVARIANT: session_id is globally unique (generateSessionID uses 8 random
 * bytes + timestamp). If this ever changes, GROUP BY must include project_id.
 */
export function aggregateTokensBySessionAll(opts?: {
  sinceMs?: number;
}): Map<string, SessionTokenAggregate> {
  const sinceMs = opts?.sinceMs ?? 0;
  const result = new Map<string, SessionTokenAggregate>();

  // 1. Token sums per session (all projects).
  const tokenRows = db()
    .query(
      `SELECT session_id, SUM(tokens) as total_tokens
       FROM temporal_messages
       WHERE created_at >= ?
       GROUP BY session_id`,
    )
    .all(sinceMs) as Array<{ session_id: string; total_tokens: number }>;
  for (const row of tokenRows) {
    result.set(row.session_id, {
      session_id: row.session_id,
      total_tokens: row.total_tokens ?? 0,
      first_assistant_metadata: null,
    });
  }

  // 2. Earliest assistant message metadata per session (for model detection).
  //    Uses a JOIN on a pre-grouped MIN subquery instead of a correlated
  //    subquery — the correlated form is O(N²) on large tables (178K+ rows).
  const metaRows = db()
    .query(
      `SELECT t.session_id, t.metadata
       FROM temporal_messages t
       JOIN (
         SELECT session_id, MIN(created_at) AS min_at
         FROM temporal_messages
         WHERE role = 'assistant' AND created_at >= ?
         GROUP BY session_id
       ) m ON m.session_id = t.session_id AND t.created_at = m.min_at
       WHERE t.role = 'assistant'
       GROUP BY t.session_id`,
    )
    .all(sinceMs) as Array<{
    session_id: string;
    metadata: string;
  }>;
  for (const row of metaRows) {
    const existing = result.get(row.session_id);
    if (existing) {
      existing.first_assistant_metadata = row.metadata;
    } else {
      result.set(row.session_id, {
        session_id: row.session_id,
        total_tokens: 0,
        first_assistant_metadata: row.metadata,
      });
    }
  }

  return result;
}

export function markDistilled(ids: string[]) {
  if (!ids.length) return;
  const placeholders = ids.map(() => "?").join(",");
  db()
    .query(
      `UPDATE temporal_messages SET distilled = 1 WHERE id IN (${placeholders})`,
    )
    .run(...ids);
}

// Searches all temporal messages (including distilled). Distilled messages
// are kept in BM25 search because distillation may compress away specific
// identifiers (branch names, file paths, test counts) that keyword search
// needs to find. RRF handles deduplication via separate key prefixes.
// LIKE-based fallback for when FTS5 fails unexpectedly.
function searchLike(input: {
  pid: string;
  query: string;
  sessionID?: string;
  limit: number;
}): TemporalMessage[] {
  const terms = input.query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 2);
  if (!terms.length) return [];
  const conditions = terms.map(() => "LOWER(content) LIKE ?").join(" AND ");
  const likeParams = terms.map((t) => `%${t}%`);
  const query = input.sessionID
    ? `SELECT * FROM temporal_messages WHERE project_id = ? AND session_id = ? AND ${conditions} ORDER BY created_at DESC LIMIT ?`
    : `SELECT * FROM temporal_messages WHERE project_id = ? AND ${conditions} ORDER BY created_at DESC LIMIT ?`;
  const params = input.sessionID
    ? [input.pid, input.sessionID, ...likeParams, input.limit]
    : [input.pid, ...likeParams, input.limit];
  return db()
    .query(query)
    .all(...params) as TemporalMessage[];
}

export function search(input: {
  projectPath: string;
  query: string;
  sessionID?: string;
  limit?: number;
}): TemporalMessage[] {
  const pid = ensureProject(input.projectPath);
  const limit = input.limit ?? 20;

  const ftsSQL = input.sessionID
    ? `SELECT m.* FROM temporal_fts f
       CROSS JOIN temporal_messages m ON m.rowid = f.rowid
       WHERE f.content MATCH ? AND m.project_id = ? AND m.session_id = ?
       ORDER BY rank LIMIT ?`
    : `SELECT m.* FROM temporal_fts f
       CROSS JOIN temporal_messages m ON m.rowid = f.rowid
       WHERE f.content MATCH ? AND m.project_id = ?
       ORDER BY rank LIMIT ?`;

  try {
    return runRelaxedSearch(input.query, (matchExpr) => {
      const params = input.sessionID
        ? [matchExpr, pid, input.sessionID, limit]
        : [matchExpr, pid, limit];
      return db()
        .query(ftsSQL)
        .all(...params) as TemporalMessage[];
    });
  } catch {
    // FTS5 still choked (edge case) — fall back to LIKE search
    return searchLike({
      pid,
      query: input.query,
      sessionID: input.sessionID,
      limit,
    });
  }
}

export type ScoredTemporalMessage = TemporalMessage & { rank: number };

/**
 * Search with BM25 scores included. Returns results with raw FTS5 rank values
 * for use in cross-source score fusion (RRF).
 */
export async function searchScored(input: {
  projectPath: string;
  query: string;
  sessionID?: string;
  limit?: number;
  /** IDF weights from `termIDF()` — when provided, the relaxed cascade
   *  drops common terms first instead of short ones. */
  termWeights?: Map<string, number>;
}): Promise<ScoredTemporalMessage[]> {
  const pid = ensureProject(input.projectPath);
  const limit = input.limit ?? 20;

  // Lean column list (NOT `m.*`): the `embedding` BLOB must never cross the
  // read-worker boundary (read-job contract in read-job.ts), and selecting it
  // is pure waste even in-process — `ScoredTemporalMessage` has no embedding
  // field. Mirrors the hydration SELECT in recall.ts.
  const ftsSQL = input.sessionID
    ? `SELECT m.id, m.project_id, m.session_id, m.role, m.content, m.tokens,
              m.distilled, m.created_at, m.metadata, rank
       FROM temporal_fts f
       CROSS JOIN temporal_messages m ON m.rowid = f.rowid
       WHERE f.content MATCH ? AND m.project_id = ? AND m.session_id = ?
       ORDER BY rank LIMIT ?`
    : `SELECT m.id, m.project_id, m.session_id, m.role, m.content, m.tokens,
              m.distilled, m.created_at, m.metadata, rank
       FROM temporal_fts f
       CROSS JOIN temporal_messages m ON m.rowid = f.rowid
       WHERE f.content MATCH ? AND m.project_id = ?
       ORDER BY rank LIMIT ?`;

  try {
    return await runRelaxedSearchAsync(
      input.query,
      async (matchExpr) => {
        const params = input.sessionID
          ? [matchExpr, pid, input.sessionID, limit]
          : [matchExpr, pid, limit];
        // Staleness-tolerant FTS scan over the largest table (temporal_messages)
        // — the real 3.5–7.5s main-thread blocker (#966 B). Offload it; a worker
        // timeout aborts the cascade (null) instead of re-blocking the loop.
        const rows = await offloadAllOrTimeout(ftsSQL, params);
        if (rows === READ_JOB_TIMED_OUT) return null;
        return rows as ScoredTemporalMessage[];
      },
      input.termWeights,
    );
  } catch {
    return [];
  }
}

/**
 * Normalized variance of relative-existence weights over message timestamps.
 *
 * Measures temporal attention imbalance: 0 means timestamps are evenly
 * distributed (uniform attention), 1 means a single distant timestamp
 * dominates (attention stuck in the past). Useful as a lightweight
 * signal for distillation segmentation, recall time-biasing, and
 * idle-resume awareness.
 *
 * Only meaningful for n ≥ 2. Returns 0 for 0 or 1 timestamps.
 *
 * Based on the "Temporal Clustering via Relative Existence" heuristic
 * from D7x7z49/llm-context-idea.
 */
export function temporalCnorm(
  timestamps: number[],
  now: number = Date.now(),
): number {
  const n = timestamps.length;
  if (n < 2) return 0;

  // Existence durations: how long each piece has existed
  const durations = timestamps.map((t) => now - t);
  const totalDuration = durations.reduce((a, b) => a + b, 0);
  if (totalDuration <= 0) return 0;

  // Relative existence weights (positive, sum to 1)
  const weights = durations.map((d) => d / totalDuration);

  // Normalized variance: Var(w) / Var_max
  // Var(w) = (1/n) * Σ(w_i - 1/n)²
  // Var_max = (n-1) / n²  (when one weight = 1, rest = 0)
  const uniform = 1 / n;
  const variance = weights.reduce((sum, w) => sum + (w - uniform) ** 2, 0) / n;
  const maxVariance = (n - 1) / (n * n);
  return maxVariance === 0 ? 0 : variance / maxVariance;
}

export function count(projectPath: string, sessionID?: string): number {
  const pid = ensureProject(projectPath);
  const query = sessionID
    ? "SELECT COUNT(*) as count FROM temporal_messages WHERE project_id = ? AND session_id = ?"
    : "SELECT COUNT(*) as count FROM temporal_messages WHERE project_id = ?";
  const params = sessionID ? [pid, sessionID] : [pid];
  return (
    db()
      .query(query)
      .get(...params) as { count: number }
  ).count;
}

/** Quick existence check — true if any temporal messages exist for this session. */
export function hasMessages(projectPath: string, sessionID: string): boolean {
  const pid = ensureProject(projectPath);
  return !!db()
    .query(
      "SELECT 1 FROM temporal_messages WHERE project_id = ? AND session_id = ? LIMIT 1",
    )
    .get(pid, sessionID);
}

export function undistilledCount(
  projectPath: string,
  sessionID?: string,
): number {
  const pid = ensureProject(projectPath);
  const query = sessionID
    ? "SELECT COUNT(*) as count FROM temporal_messages WHERE project_id = ? AND session_id = ? AND distilled = 0"
    : "SELECT COUNT(*) as count FROM temporal_messages WHERE project_id = ? AND distilled = 0";
  const params = sessionID ? [pid, sessionID] : [pid];
  return (
    db()
      .query(query)
      .get(...params) as { count: number }
  ).count;
}

/** Sum of estimated tokens across undistilled messages for a project/session. */
export function undistilledTokens(
  projectPath: string,
  sessionID?: string,
): number {
  const pid = ensureProject(projectPath);
  const query = sessionID
    ? "SELECT COALESCE(SUM(tokens), 0) as total FROM temporal_messages WHERE project_id = ? AND session_id = ? AND distilled = 0"
    : "SELECT COALESCE(SUM(tokens), 0) as total FROM temporal_messages WHERE project_id = ? AND distilled = 0";
  const params = sessionID ? [pid, sessionID] : [pid];
  return (
    db()
      .query(query)
      .get(...params) as { total: number }
  ).total;
}

export type PruneResult = {
  /** Rows deleted by the TTL pass (distilled=1 AND older than retention period). */
  ttlDeleted: number;
  /** Rows deleted by the size-cap pass (distilled=1, oldest-first, to get under maxStorage). */
  capDeleted: number;
};

/**
 * Prune temporal messages for a project using a two-pass Hybrid C strategy:
 *
 * Pass 1 — TTL: delete messages where distilled=1 AND created_at is older than
 * retentionDays. This covers normal operation — both distillation and curation
 * have had ample time to process anything that old.
 *
 * Pass 2 — Size cap: if total temporal storage for the project still exceeds
 * maxStorageMB, delete the oldest distilled=1 messages (regardless of age)
 * until under the cap.
 *
 * Invariant: undistilled messages (distilled=0) are NEVER deleted by either pass.
 *
 * Resilience (#1001): the active `db()` connection is reset/rebuilt several
 * times per process run (overflow-recovery / split / magnet maintenance) and an
 * idle prune can race that window, observing a connection where a table is
 * momentarily absent (`SQLiteError: no such table: distillations`). The
 * connection/project setup and each pass are therefore wrapped so a
 * *missing-object* error is a no-op for that tick (the next idle tick reruns it)
 * instead of aborting the remaining passes. Any other error still propagates to
 * the caller. A *persistent* run of such skips (>= MISSING_OBJECT_ESCALATE_TICKS
 * consecutive ticks) escalates from info to warn, since a sustained miss points
 * at a real schema problem rather than a transient swap. Root-causing/serializing
 * the swap itself and recovering missing base tables are tracked follow-ups.
 */
/**
 * Clear sync_state for locally-pruned Pro-backup rows (#826/D). The prune is
 * sync-INVISIBLE: the remote backup is bounded by the server reaper + per-scope
 * quota, NOT by local residency, so a pruned row must never tombstone the remote
 * (reconcile also skips these append-only tables). Dropping the now-dead sync_state
 * keeps it from growing unbounded as messages are pruned over a device's lifetime.
 * Harmless when sync is off (no rows) or the table is un-synced (basic tier).
 */
function clearPrunedSyncState(
  database: ReturnType<typeof db>,
  table: string,
  ids: string[],
): void {
  if (ids.length === 0) return;
  const ph = ids.map(() => "?").join(",");
  database
    .query(`DELETE FROM sync_state WHERE table_name = ? AND row_id IN (${ph})`)
    .run(table, ...ids);
}

export function prune(input: {
  projectPath: string;
  retentionDays: number;
  maxStorageMB: number;
}): PruneResult {
  // Resolving the connection and project row can itself race the swap window
  // (e.g. `no such table: projects` from ensureProject's lookup); treat that the
  // same as a per-pass miss — skip this tick and let the next one retry (#1001).
  let database: ReturnType<typeof db>;
  let pid: string;
  try {
    database = db();
    pid = ensureProject(input.projectPath);
  } catch (e) {
    if (!isMissingObjectError(e)) throw e;
    log.info(
      "temporal.prune setup (db/ensureProject) skipped — object missing during db maintenance window (transient):",
      e,
    );
    noteMissingObjectTick();
    return { ttlDeleted: 0, capDeleted: 0 };
  }
  const cutoff = Date.now() - input.retentionDays * 24 * 60 * 60 * 1000;
  // Tracks whether any pass skipped on a missing object this tick, so a sustained
  // run of skips can escalate from info to warn (see noteMissingObjectTick).
  let sawMissingObject = false;

  // Pass 1: TTL — delete distilled messages older than the retention window.
  // Note: result.changes is inflated by FTS trigger side-effects, so we count
  // eligible rows before deletion to get the accurate number deleted.
  let ttlDeleted = 0;
  try {
    // Select the ids first (instead of a bare COUNT) so we can also drop the
    // messages' vec0 chunks: in vec0 mode the vectors live in the separate
    // `temporal_vec` virtual table, so the base-row DELETE below leaves them
    // dangling. `deleteEmbeddings` is a no-op in blob mode and when the list is
    // empty. (The startup `gcVec0DanglingRows` sweep remains the catch-all
    // backstop for orphans from other paths / a crash mid-delete.)
    // COALESCE(restored_at, created_at) (#826/D): a restored/pulled message keeps its
    // origin created_at for ordering/recall but ages out on its LOCAL residency clock,
    // so it survives a full retention window instead of being evicted immediately for
    // its old origin created_at (B3). restored_at is NULL for native rows (created_at).
    const ttlIds = (
      database
        .query(
          "SELECT id FROM temporal_messages WHERE project_id = ? AND distilled = 1 AND COALESCE(restored_at, created_at) < ?",
        )
        .all(pid, cutoff) as { id: string }[]
    ).map((r) => r.id);
    if (ttlIds.length > 0) {
      // Capture-suppressed (#826/D): defense-in-depth so a future sync DELETE
      // trigger can't tombstone the remote backup; also drops the dead sync_state.
      withSyncApplying(() => {
        database
          .query(
            "DELETE FROM temporal_messages WHERE project_id = ? AND distilled = 1 AND COALESCE(restored_at, created_at) < ?",
          )
          .run(pid, cutoff);
        clearPrunedSyncState(database, "temporal_messages", ttlIds);
      });
      deleteEmbeddings(database, "temporal", ttlIds);
    }
    ttlDeleted = ttlIds.length;
  } catch (e) {
    if (!isMissingObjectError(e)) throw e;
    sawMissingObject = true;
    log.info(
      "temporal.prune Pass 1 (TTL) skipped — object missing during db maintenance window (transient):",
      e,
    );
  }

  // Pass 2: Size cap — check if total storage for this project exceeds the
  // limit and if so, evict the oldest distilled messages until under the cap.
  let capDeleted = 0;
  try {
    const maxBytes = input.maxStorageMB * 1024 * 1024;
    const totalBytes =
      (
        database
          .query(
            "SELECT SUM(LENGTH(content)) as b FROM temporal_messages WHERE project_id = ?",
          )
          .get(pid) as { b: number | null }
      ).b ?? 0;

    if (totalBytes > maxBytes) {
      // Collect oldest distilled messages until we've accounted for enough bytes
      // to drop below the cap. Delete them in a single batch.
      const candidates = database
        .query(
          // Evict by LOCAL residency (COALESCE(restored_at, created_at)) so a freshly
          // restored message isn't the first evicted for its old origin created_at (#826/D).
          "SELECT id, LENGTH(content) as size FROM temporal_messages WHERE project_id = ? AND distilled = 1 ORDER BY COALESCE(restored_at, created_at) ASC",
        )
        .all(pid) as { id: string; size: number }[];

      const toDelete: string[] = [];
      let freed = 0;
      const excess = totalBytes - maxBytes;
      for (const row of candidates) {
        if (freed >= excess) break;
        toDelete.push(row.id);
        freed += row.size;
      }

      if (toDelete.length) {
        const placeholders = toDelete.map(() => "?").join(",");
        withSyncApplying(() => {
          database
            .query(
              `DELETE FROM temporal_messages WHERE id IN (${placeholders})`,
            )
            .run(...toDelete);
          clearPrunedSyncState(database, "temporal_messages", toDelete);
        });
        // Drop the evicted messages' vec0 chunks too (see Pass 1 rationale).
        deleteEmbeddings(database, "temporal", toDelete);
        // toDelete.length is the accurate count — result.changes is inflated by FTS triggers.
        capDeleted = toDelete.length;
      }
    }
  } catch (e) {
    if (!isMissingObjectError(e)) throw e;
    sawMissingObject = true;
    log.info(
      "temporal.prune Pass 2 (size cap) skipped — object missing during db maintenance window (transient):",
      e,
    );
  }

  // Pass 3: Prune archived distillations older than the retention window.
  // Archived gen-0 distillations are kept for recall search but don't need
  // to live forever — they follow the same retention policy as temporal messages.
  try {
    // Select ids first so the matching `distillation_vec` chunks can be dropped
    // alongside the base rows (same vec0-orphan reasoning as Pass 1).
    const archivedIds = (
      database
        .query(
          "SELECT id FROM distillations WHERE project_id = ? AND archived = 1 AND created_at < ?",
        )
        .all(pid, cutoff) as { id: string }[]
    ).map((r) => r.id);
    if (archivedIds.length > 0) {
      withSyncApplying(() => {
        database
          .query(
            "DELETE FROM distillations WHERE project_id = ? AND archived = 1 AND created_at < ?",
          )
          .run(pid, cutoff);
        clearPrunedSyncState(database, "distillations", archivedIds);
      });
      deleteEmbeddings(database, "distillations", archivedIds);
    }
  } catch (e) {
    if (!isMissingObjectError(e)) throw e;
    sawMissingObject = true;
    log.info(
      "temporal.prune Pass 3 (archived distillations) skipped — object missing during db maintenance window (transient):",
      e,
    );
  }

  // A clean tick (no missing-object skip) clears the consecutive-skip streak so
  // only a *sustained* run of skips escalates to warn.
  if (sawMissingObject) noteMissingObjectTick();
  else consecutiveMissingObjectSkipTicks = 0;
  return { ttlDeleted, capDeleted };
}

/**
 * True when a thrown DB error is a transient missing-object error (`no such
 * table` / `no such column`), the symptom of racing a `db()` reset/rebuild
 * maintenance window (#1001). Such errors are swallowed per-pass in `prune()`;
 * everything else propagates.
 */
function isMissingObjectError(err: unknown): boolean {
  return err instanceof Error && /no such (table|column)/i.test(err.message);
}

/**
 * Number of consecutive prune() ticks that skipped one or more passes on a
 * missing object before we escalate from info to warn. A single skip is the
 * expected, benign symptom of racing a db() maintenance swap (#1001); a sustained
 * run instead points at a persistent schema problem (a dropped/renamed object,
 * an un-run migration) that would otherwise let prune silently no-op forever
 * while temporal storage grows past its cap.
 */
const MISSING_OBJECT_ESCALATE_TICKS = 3;
let consecutiveMissingObjectSkipTicks = 0;

/**
 * Record a prune() tick that skipped at least one pass on a missing object,
 * escalating from info to warn once the skip becomes persistent rather than
 * transient. Called at most once per prune() tick.
 */
function noteMissingObjectTick(): void {
  consecutiveMissingObjectSkipTicks++;
  if (consecutiveMissingObjectSkipTicks >= MISSING_OBJECT_ESCALATE_TICKS) {
    log.warn(
      `temporal.prune has skipped passes on missing DB objects for ${consecutiveMissingObjectSkipTicks} consecutive ticks — ` +
        "expected briefly during db() maintenance swaps, but a sustained recurrence indicates a persistent schema problem (#1001).",
    );
  }
}
